import type { ClientTarget } from '@features/clients';

import {
    glGapsFileName,
    glGapsObservation,
    MEMBERSHIP_PROGRAMS_MODULE_ID,
    toGlGapsCsv,
} from './membership-gl-gaps';

import type { JobHeader, TenantRow } from '../schemas/bulk-migration';

/**
 * What a module adds to the analysis screen, so `DryRunView` stays generic: a
 * one-line summary over the clients loaded so far, and one more download. A
 * module with nothing to add answers `null`.
 */

export interface DryRunExtras {
    /** `null` when there is nothing worth a line. */
    readonly summary: (rows: readonly TenantRow[]) => string | null;
    readonly csv: {
        readonly fileName: (job: Pick<JobHeader, 'moduleId' | 'reportId'>) => string;
        readonly content: (job: JobHeader, rows: readonly TenantRow[]) => string;
    };
    readonly buttonLabel: string;
}

const MEMBERSHIP_PROGRAMS_EXTRAS: DryRunExtras = {
    // Over the LOADED rows (one page, under the outcome filter): the CSV reads them all.
    summary: (rows) => {
        const observation = glGapsObservation(rows);
        return observation === null ? null : `Loaded clients: ${observation.text}`;
    },
    csv: { fileName: glGapsFileName, content: toGlGapsCsv },
    buttonLabel: 'Download GL gaps (CSV)',
};

export function dryRunExtras(moduleId: string): DryRunExtras | null {
    return moduleId === MEMBERSHIP_PROGRAMS_MODULE_ID ? MEMBERSHIP_PROGRAMS_EXTRAS : null;
}

/** `readAllRows`'s shape: every page of a job's rows, under an optional outcome filter. */
export type ReadAllRows = (
    target: ClientTarget,
    moduleId: string,
    reportId: string,
    outcome?: string,
) => Promise<TenantRow[]>;

/**
 * The module's CSV over EVERY client of the analysis: all pages, and no outcome
 * filter, whatever the screen has loaded or filtered.
 */
export async function extrasCsvFile(
    extras: DryRunExtras,
    target: ClientTarget,
    job: JobHeader,
    readAll: ReadAllRows,
): Promise<{ fileName: string; content: string }> {
    const all = await readAll(target, job.moduleId, job.reportId);
    return { fileName: extras.csv.fileName(job), content: extras.csv.content(job, all) };
}
